import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { GridColDef } from '@mui/x-data-grid/models';
import { Box, IconButton, Tooltip, Button, Badge } from '@mui/material';
import { Add, Search, Refresh, GetApp } from '@mui/icons-material';
import { ItemResponse, SearchItemsRequest, ACLAuthorizationRequest } from '@/api/bol';
import { parseJsonWithDates } from '@/utils/jsonUtils';
import { 
  PageLayout, 
  DataGridContainer, 
  JsonDetailPanel,
  SearchPanel,
  createItemColumns,
  defaultItemColumnVisibilityModel,
  ItemFormDialog,
  ItemDeleteDialog,
  GenericCSVExportDialog,
} from '@/components';
import { 
  useDataGridSelection, 
  usePagination, 
  useSnackbarNotification,
  useItems,
  useStableRowId,
  usePermissions,
  useLogisticsObjectSearchUrl,
  getActionTooltip
} from '@/hooks';
import { exportToCSV, generateCSVFilename } from '@/utils/csvExport';
import { DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY } from '@/utils/searchDefaults';
import { getDefaultSavedQuery } from '@/utils/savedQueriesStorage';

const ItemsPage: React.FC = () => {
  const { can } = usePermissions();
  const {
    getSearchQueryFromUrl,
    updateSearchQueryInUrl,
    copyShareableSearchLink,
  } = useLogisticsObjectSearchUrl({ queryParamName: 'search' });
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const initialSearchQuery = useMemo<SearchItemsRequest>(
    () => {
      const searchQueryFromUrl = getSearchQueryFromUrl() as SearchItemsRequest | undefined;

      return searchQueryFromUrl
        || getDefaultSavedQuery('item')?.query as SearchItemsRequest
        || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    },
    [getSearchQueryFromUrl],
  );
  const [currentSearchQuery, setCurrentSearchQuery] = useState<SearchItemsRequest>(initialSearchQuery);

  const hasAppliedQueryOrSort = useMemo(() => {
    const hasQueryContent = !!currentSearchQuery?.query
      && typeof currentSearchQuery.query === 'object'
      && Object.keys(currentSearchQuery.query).length > 0;
    const hasSortContent = Array.isArray(currentSearchQuery?.sort)
      && currentSearchQuery.sort.length > 0;

    return hasQueryContent || hasSortContent;
  }, [currentSearchQuery]);
  
  const displayColumnsRef = useRef<{ columns: GridColDef[]; visibilityModel: Record<string, boolean> }>({
    columns: [],
    visibilityModel: defaultItemColumnVisibilityModel,
  });

  const handleDisplayColumnsChange = useCallback((cols: GridColDef[], visibilityModel: Record<string, boolean>) => {
    displayColumnsRef.current = { columns: cols, visibilityModel };
  }, []);

  const getRowId = useStableRowId<ItemResponse>('item');

  // Use custom hooks for item management
  const {
    items,
    loading,
    selectedItem,
    setSelectedItem,
    fetchItems,
    createItem,
    deleteItem,
    total,
  } = useItems();

  // Use custom hooks for common functionality
  const { 
    selectedItem: selectedItemForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection<ItemResponse>({ 
    data: items, 
    getItemId: (item) => item.id || '' 
  });

  const {
    pagination,
    handlePaginationModelChange,
  } = usePagination({
    onPageChange: (page, pageSize) => {
      void fetchItems(page, pageSize, currentSearchQuery);
    }
  });

  const {
    snackbarOpen,
    snackbarMessage,
    snackbarLink,
    snackbarType,
    showSuccess,
    showError,
    closeSnackbar
  } = useSnackbarNotification();

  // Event handlers
  const handleDeleteClick = (item: ItemResponse) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleRefresh = () => {
    fetchItems(pagination.page, pagination.pageSize, currentSearchQuery);
  };

  const handleSearch = async (searchData: unknown) => {
    const searchBody = searchData as SearchItemsRequest;
    setCurrentSearchQuery(searchBody);
    const isSearchSuccessful = await fetchItems(0, pagination.pageSize, searchBody); // Reset to first page on search

    if (isSearchSuccessful) {
      updateSearchQueryInUrl(searchBody);
    }
  };

  const handleSearchReset = async () => {
    const defaultSearchQuery = (getDefaultSavedQuery('item')?.query as SearchItemsRequest)
      || DEFAULT_LOGISTICS_OBJECT_SEARCH_QUERY;
    setCurrentSearchQuery(defaultSearchQuery);
    const isResetSuccessful = await fetchItems(0, pagination.pageSize, defaultSearchQuery); // Reset to first page with default search

    if (isResetSuccessful) {
      updateSearchQueryInUrl(defaultSearchQuery);
    }
  };

  const handleCopySearchLink = useCallback(async () => {
    await copyShareableSearchLink(currentSearchQuery, {
      onSuccess: showSuccess,
      onError: showError,
    });
  }, [copyShareableSearchLink, currentSearchQuery, showError, showSuccess]);

  const handleToggleSearchPanel = () => {
    setSearchPanelOpen(!searchPanelOpen);
  };

  const handleExportCSV = (separator: string) => {
    exportToCSV({
      data: items,
      columns: displayColumnsRef.current.columns,
      columnVisibilityModel: displayColumnsRef.current.visibilityModel,
      filename: generateCSVFilename('items'),
      separator,
    });
  };

  const handleCreateItem = async (data: { memberIdentifier: string; itemJson: string; aclAuthorizations?: ACLAuthorizationRequest[] }) => {
    try {
      const { memberIdentifier, itemJson, aclAuthorizations } = data;

      // Validate required fields
      if (!memberIdentifier?.trim()) {
        throw new Error('Member identifier is required');
      }
      
      if (!itemJson?.trim()) {
        throw new Error('Item JSON is required');
      }

      // Parse and validate JSON
      let itemData;
      try {
        itemData = parseJsonWithDates(itemJson);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        throw new Error('Invalid JSON format. Please check your JSON syntax.');
      }

      // Validate parsed data structure
      if (!itemData || typeof itemData !== 'object') {
        throw new Error('Item data must be a valid JSON object');
      }

      await createItem(itemData, memberIdentifier, aclAuthorizations);
      setCreateDialogOpen(false);
      // Refresh the list after successful creation
      fetchItems(pagination.page, pagination.pageSize, currentSearchQuery);
    } catch (error) {
      // Error is handled by the hook, but we can add additional client-side validation errors
      console.error('Item creation validation error:', error);
      throw error; // Re-throw to let the dialog handle the error display
    }
  };

  const handleDeleteItem = async () => {
    if (!selectedItem?.id) return;

    await deleteItem(selectedItem.id);
    setSelectedItem(null);
    // Refresh the list after successful deletion
    fetchItems(pagination.page, pagination.pageSize, currentSearchQuery);
  };

  // Create columns configuration
  const columns = createItemColumns({
    onDelete: handleDeleteClick,
    canDelete: can.deleteLogisticsObject,
    deleteTooltip: getActionTooltip('deleteLogisticsObject')
  });

  useEffect(() => {
    fetchItems(0, 10, initialSearchQuery);
  }, [fetchItems, initialSearchQuery]);

  return (
    <PageLayout 
      title="Items Management"
      selectedItemInfo={selectedItemForDetail ? `Selected: ${selectedItemForDetail.id}` : undefined}
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationLink={snackbarLink}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
      leftTitleActions={
        <>
          <Tooltip title="Refresh items list">
            <IconButton
              onClick={handleRefresh}
              size="small"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Tooltip title={searchPanelOpen ? "Close search panel" : "Open search panel"}>
            <IconButton
              onClick={handleToggleSearchPanel}
              color={searchPanelOpen ? "primary" : "default"}
              size="small"
            >
              <Badge color="secondary" variant="dot" invisible={!hasAppliedQueryOrSort}>
                <Search />
              </Badge>
            </IconButton>
          </Tooltip>
        </>
      }
      titleActions={
          <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<GetApp />}
            onClick={() => setExportDialogOpen(true)}
            size="medium"
          >
            Export CSV
          </Button>
          <Tooltip title={!can.createLogisticsObject ? getActionTooltip('createLogisticsObject') : ''}>
          <span>
          <Button
                variant="contained"
                size="medium"
                onClick={() => setCreateDialogOpen(true)}
                startIcon={<Add />}
              disabled={!can.createLogisticsObject}
              >
                Create Item
              </Button>
          </span>
        </Tooltip>
        </Box>
      }
      leftPanel={searchPanelOpen ? (
        <SearchPanel
          title="Search Items"
          onClose={() => setSearchPanelOpen(false)}
          onSearch={handleSearch}
          onReset={handleSearchReset}
          onCopyLink={handleCopySearchLink}
          loading={loading}
          logisticsObjectType="item"
          currentSearchQuery={currentSearchQuery}
        />
      ) : undefined}
      sidePanel={selectedItemForDetail ? (
        <JsonDetailPanel
          title="Item Details"
          data={selectedItemForDetail}
          onClose={handleCloseDetailPanel}
          onCopyError={() => {}} // Error handling moved to hook
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <DataGridContainer
          rows={items}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={total}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={getRowId}
          columnVisibilityModel={defaultItemColumnVisibilityModel}
          columnOrderStorageKey="items-columns"
          onDisplayColumnsChange={handleDisplayColumnsChange}
        />
      </Box>

      {/* Create Item Dialog */}
      <ItemFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateItem}
      />

      {/* Delete Confirmation Dialog */}
      <ItemDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteItem}
        item={selectedItem}
      />

      {/* CSV Export Dialog */}
      <GenericCSVExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onSubmit={handleExportCSV}
        title="Export Items to CSV"
        entityName="items"
        defaultFilename={generateCSVFilename('items')}
        currentPageInfo={{
          page: pagination.page,
          pageSize: pagination.pageSize,
          totalRows: total,
        }}
      />
    </PageLayout>
  );
};

export default ItemsPage;